import React from 'react';
import { WidgetConfig } from '@/components/dashboard/DraggableWidget';
import { AnimatedKpiCard } from '@/components/analytics/AnimatedKpiCard';
import { BestSellersTable } from '@/components/analytics/BestSellersTable';
import { SalesChart } from '@/components/analytics/SalesChart';
import { SalesPieChart } from '@/components/analytics/SalesPieChart';
import { WithdrawalsChart } from '@/components/analytics/WithdrawalsChart';
import { WithdrawalsPieChart } from '@/components/analytics/WithdrawalsPieChart';
import { TopWithdrawnProductsTable } from '@/components/analytics/TopWithdrawnProductsTable';
import { SalesListTable } from '@/components/analytics/SalesListTable';
import { InfiniteSalesListTable } from '@/components/analytics/InfiniteSalesListTable';
import { DollarSign, Package, TrendingUp, RotateCcw, Minus, TrendingDown } from 'lucide-react';

interface AnalyticsWidgetProps {
  widget: WidgetConfig;
  kpiData?: any;
  filteredData?: any[];
  periodFilter?: any;
  isLoading?: boolean;
  userRole?: string;
  onStatusFilterChange?: (filter: any) => void;
  statusFilter?: string;
  hasAccess?: boolean;
  productById?: Record<string, any>;
  products?: any[];
  currentPage?: number;
  rowsPerPage?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  onRowsPerPageChange?: (rows: number) => void;
}

export const AnalyticsWidget: React.FC<AnalyticsWidgetProps> = ({
  widget,
  kpiData,
  filteredData = [],
  periodFilter,
  isLoading = false,
  userRole,
  onStatusFilterChange,
  statusFilter,
  hasAccess,
  productById,
  products = [],
  currentPage = 1,
  rowsPerPage = 20,
  totalPages = 1,
  onPageChange,
  onRowsPerPageChange,
}) => {
  const getPeriodLabel = () => {
    switch (periodFilter) {
      case 'today':
        return "aujourd'hui";
      case 'week':
        return 'sur 7 jours';
      case 'month':
        return 'sur 30 jours';
      default:
        return 'depuis toujours';
    }
  };

  const renderKpiWidget = () => {
    const { kpiType } = widget.config || {};
    
    switch (kpiType) {
      case 'revenue':
        return (
          <AnimatedKpiCard
            title="Chiffre d'affaires"
            value={kpiData?.totalRevenue || 0}
            subtitle={getPeriodLabel()}
            icon={DollarSign}
            format="currency"
            colorScheme="green"
            isLoading={isLoading}
          />
        );
      case 'products-sold':
        return (
          <AnimatedKpiCard
            title="Produits vendus"
            value={kpiData?.totalItemsSold || 0}
            subtitle="Quantité totale"
            icon={Package}
            format="number"
            colorScheme="purple"
            isLoading={isLoading}
          />
        );
      case 'average-basket':
        return (
          <AnimatedKpiCard
            title="Panier moyen"
            value={kpiData?.averageBasket || 0}
            subtitle="CA / vente"
            icon={TrendingUp}
            format="currency"
            colorScheme="orange"
            isLoading={isLoading}
          />
        );
      case 'total-withdrawals':
        return (
          <AnimatedKpiCard
            title="Total retraits"
            value={kpiData?.totalRetraits || 0}
            subtitle={getPeriodLabel()}
            icon={RotateCcw}
            format="number"
            colorScheme="orange"
            isLoading={isLoading}
          />
        );
      case 'items-withdrawn':
        return (
          <AnimatedKpiCard
            title="Articles retirés"
            value={kpiData?.totalItemsRetired || 0}
            subtitle="Quantité totale"
            icon={Minus}
            format="number"
            colorScheme="red"
            isLoading={isLoading}
          />
        );
      case 'products-concerned':
        return (
          <AnimatedKpiCard
            title="Produits concernés"
            value={kpiData?.uniqueProducts || 0}
            subtitle="Types de produits"
            icon={Package}
            format="number"
            colorScheme="purple"
            isLoading={isLoading}
          />
        );
      case 'average-per-product':
        return (
          <AnimatedKpiCard
            title="Moyenne par produit"
            value={kpiData?.averageRetraitPerProduct || 0}
            subtitle="Articles/produit"
            icon={TrendingDown}
            format="number"
            colorScheme="blue"
            isLoading={isLoading}
          />
        );
      default:
        return <div className="p-4 text-muted-foreground">KPI non configuré</div>;
    }
  };

  const renderChartWidget = () => {
    const { chartType } = widget.config || {};
    
    switch (chartType) {
      case 'sales-chart':
        return <SalesChart sales={filteredData} />;
      case 'sales-pie':
        return <SalesPieChart sales={filteredData} productById={productById} periodLabel={getPeriodLabel()} />;
      case 'withdrawals-chart':
        return <WithdrawalsChart withdrawals={filteredData} />;
      case 'withdrawals-pie':
        return <WithdrawalsPieChart withdrawals={filteredData} productById={productById} periodLabel={getPeriodLabel()} />;
      default:
        return <div className="p-4 text-muted-foreground">Graphique non configuré</div>;
    }
  };

  const renderTableWidget = () => {
    const { tableType } = widget.config || {};
    
    switch (tableType) {
      case 'best-sellers':
        return <BestSellersTable periodFilter={periodFilter} />;
      case 'top-withdrawn-products':
        return <TopWithdrawnProductsTable withdrawals={filteredData} productById={productById} />;
      case 'sales-list':
        // Utiliser InfiniteSalesListTable si dans un modal avec scroll infini
        if ((widget as any).useInfiniteScroll) {
          return (
            <InfiniteSalesListTable
              sales={filteredData}
              productById={productById}
              statusFilter={statusFilter}
              onStatusFilterChange={onStatusFilterChange}
              hasAccess={hasAccess}
              showComparativeStats={(widget as any).showComparativeStats}
            />
          );
        }
        return (
          <SalesListTable
            sales={filteredData}
            productById={productById}
            statusFilter={statusFilter}
            onStatusFilterChange={onStatusFilterChange}
            hasAccess={hasAccess}
            currentPage={currentPage}
            rowsPerPage={rowsPerPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
            onRowsPerPageChange={onRowsPerPageChange}
          />
        );
      default:
        return <div className="p-4 text-muted-foreground">Tableau non configuré</div>;
    }
  };

  switch (widget.type) {
    case 'kpi':
      return renderKpiWidget();
    case 'chart':
      return renderChartWidget();
    case 'table':
      return renderTableWidget();
    default:
      return <div className="p-4 text-muted-foreground">Type de widget inconnu</div>;
  }
};